// ConfidentialClientAuthDebugger.tsx
// A diagnostic tool for testing confidential client (web app) OAuth flows
// Shows real tokens for inspection - FOR TESTING ONLY, not production pattern

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { Application } from '@microsoft/microsoft-graph-types';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  AlertTitle,
  Divider,
  Stack,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  Autocomplete,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  VpnKey as VpnKeyIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Timeline as TimelineIcon,
  CompareArrows as CompareArrowsIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Apps as AppIcon,
  Web as WebIcon,
  MobileScreenShare as MobileScreenShareIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';

interface ConfidentialClientAuthDebuggerProps {
  onBack: () => void;
  applications: Application[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SearchableApplication {
  id: string;
  appId: string;
  displayName: string;
  signInAudience: string;
  appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown';
  hasSecrets: boolean;
  hasCredentials: boolean;
  tags: string[];
  searchText: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ConfidentialClientAuthDebugger: React.FC<ConfidentialClientAuthDebuggerProps> = ({ onBack, applications }) => {
  const { instance, accounts } = useMsal();
  
  // App selection
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  
  // Configuration
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [redirectUri, setRedirectUri] = useState(`${window.location.protocol}//${window.location.host}/confidential-auth-callback.html`);
  const [scopes, setScopes] = useState('openid profile offline_access');
  const [customApiAppIdUri, setCustomApiAppIdUri] = useState('');
  const [customApiScopes, setCustomApiScopes] = useState('');
  const [useCustomApi, setUseCustomApi] = useState(false);
  
  // Flow state
  const [activeStep, setActiveStep] = useState(0);
  const [authorizationUrl, setAuthorizationUrl] = useState('');
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [state, setState] = useState('');
  const [nonce, setNonce] = useState('');
  const [codeVerifier, setCodeVerifier] = useState('');
  const [codeChallenge, setCodeChallenge] = useState('');
  const [usePKCE, setUsePKCE] = useState(true);
  
  // Token response
  const [tokenResponse, setTokenResponse] = useState<any>(null);
  const [exchanging, setExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  
  // Search state
  const [searchableApps, setSearchableApps] = useState<SearchableApplication[]>([]);
  const [selectedSearchApp, setSelectedSearchApp] = useState<SearchableApplication | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  const popupRef = useRef<Window | null>(null);

  // Filter to web apps with redirect URIs
  const webApplications = useMemo(() => 
    applications.filter((app: Application) => 
      app.web?.redirectUris && app.web.redirectUris.length > 0
    ),
    [applications]
  );

  // Process applications for enhanced search
  useEffect(() => {
    const processApplications = () => {
      const processed = webApplications.map(app => {
        let appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown' = 'Web';
        
        if (app.spa?.redirectUris?.length && app.spa.redirectUris.length > 0) {
          appType = 'SPA';
        } else if ((app.api?.oauth2PermissionScopes?.length && app.api.oauth2PermissionScopes.length > 0) || (app.appRoles?.length && app.appRoles.length > 0)) {
          appType = 'API';
        } else if (app.publicClient?.redirectUris?.length && app.publicClient.redirectUris.length > 0) {
          appType = 'Mobile';
        }

        const searchText = [
          app.displayName || '',
          app.appId || '',
          app.signInAudience || '',
          appType,
          ...(app.tags || []),
          ...(app.identifierUris || [])
        ].join(' ').toLowerCase();

        return {
          id: app.id || '',
          appId: app.appId || '',
          displayName: app.displayName || 'Unnamed Application',
          signInAudience: app.signInAudience || 'Unknown',
          appType,
          hasSecrets: (app.passwordCredentials?.length || 0) > 0,
          hasCredentials: (app.keyCredentials?.length || 0) > 0,
          tags: app.tags || [],
          searchText
        } as SearchableApplication;
      });

      processed.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setSearchableApps(processed);
    };

    if (webApplications.length > 0) {
      processApplications();
    }
  }, [webApplications]);

  // Get app type icon
  const getAppTypeIcon = (appType: string) => {
    switch (appType) {
      case 'Web':
        return <WebIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'SPA':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#9c27b0' }} />;
      case 'API':
        return <ApiIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
      case 'Mobile':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#388e3c' }} />;
      default:
        return <AppIcon sx={{ fontSize: 16, color: '#757575' }} />;
    }
  };

  // Get audience color
  const getAudienceColor = (audience: string) => {
    switch (audience) {
      case 'AzureADMyOrg':
        return '#1976d2';
      case 'AzureADMultipleOrgs':
        return '#f57c00';
      case 'AzureADandPersonalMicrosoftAccount':
        return '#9c27b0';
      case 'PersonalMicrosoftAccount':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  // Enhanced application selection handler
  const handleEnhancedApplicationChange = (
    event: React.SyntheticEvent,
    newValue: SearchableApplication | null
  ) => {
    if (!newValue) {
      setSelectedSearchApp(null);
      setSelectedApp(null);
      return;
    }

    setSelectedSearchApp(newValue);
    const app = webApplications.find(a => a.id === newValue.id);
    if (app) {
      setSelectedApp(app);
      if (accounts[0]?.tenantId) {
        setSelectedTenant(accounts[0].tenantId);
      }
      
      // Set redirect URI based on current environment
      const currentOrigin = `${window.location.protocol}//${window.location.host}`;
      const callbackPath = '/confidential-auth-callback.html';
      const currentRedirectUri = `${currentOrigin}${callbackPath}`;
      
      // Check if app has a redirect URI matching current environment
      if (app.web?.redirectUris && app.web.redirectUris.length > 0) {
        const matchingUri = app.web.redirectUris.find(uri => 
          uri.startsWith(currentOrigin)
        );
        if (matchingUri) {
          setRedirectUri(matchingUri);
        } else {
          // Use current environment's callback URI even if not in app registration
          setRedirectUri(currentRedirectUri);
        }
      } else {
        setRedirectUri(currentRedirectUri);
      }
    }
  };

  // Custom filter function for better search
  const filterApplications = (options: SearchableApplication[], { inputValue }: { inputValue: string }) => {
    if (!inputValue.trim()) {
      return options.slice(0, 50);
    }

    const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);
    
    return options.filter(app => {
      return searchTerms.every(term => app.searchText.includes(term));
    }).slice(0, 100);
  };

  // Listen for callback messages
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      console.log('[Message Handler] Received message:', e.data, 'from origin:', e.origin);
      
      if (e.origin !== window.location.origin) {
        console.log('[Message Handler] Origin mismatch, ignoring');
        return;
      }
      if (!e.data || e.data.source !== 'confidential-auth-callback') {
        console.log('[Message Handler] Not from callback, ignoring');
        return;
      }

      const { payload } = e.data;
      console.log('[Message Handler] Payload:', payload);
      
      if (payload.error) {
        console.log('[Message Handler] Error in payload:', payload.error);
        let errorMessage = `${payload.error}: ${payload.error_description || ''}`;
        
        // Add helpful guidance for common custom API errors
        if (payload.error === 'invalid_resource' && payload.error_description?.includes('not found in the tenant')) {
          errorMessage += `\n\n💡 Troubleshooting Tips:\n` +
            `1. Verify the API application has 'Application ID URI' set to 'api://{clientId}' in Azure Portal\n` +
            `2. Ensure the API has exposed the scope '${customApiScopes || '.default'}' under 'Expose an API'\n` +
            `3. Grant this client app permission to access the API scope under 'API permissions'\n` +
            `4. Make sure admin consent has been granted for the API permissions`;
        } else if (payload.error === 'invalid_scope') {
          errorMessage += `\n\n💡 The scope you requested doesn't exist on the API. Check the API's 'Expose an API' section for available scopes.`;
        }
        
        setError(errorMessage);
        setActiveStep(2);
        return;
      }

      if (payload.state !== state) {
        console.log('[Message Handler] State mismatch. Expected:', state, 'Got:', payload.state);
        setError('State parameter mismatch. Possible CSRF attack.');
        return;
      }

      if (payload.code) {
        console.log('[Message Handler] Setting authorization code and moving to step 3');
        setAuthorizationCode(payload.code);
        setActiveStep(3);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [state]);

  // Generate random string for state/nonce
  const randomString = (length = 32): string => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => ('0' + b.toString(16)).slice(-2)).join('');
  };

  // Base64url encoding
  const base64urlEncode = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  // Generate PKCE challenge
  const generatePKCE = async () => {
    const verifier = randomString(43);
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const challenge = base64urlEncode(digest);
    
    setCodeVerifier(verifier);
    setCodeChallenge(challenge);
    return { verifier, challenge };
  };

  // Step 1: Generate authorization URL
  const generateAuthUrl = async () => {
    if (!selectedApp?.appId) {
      setError('Please select an application first');
      return;
    }

    setError(null);
    const newState = randomString(16);
    const newNonce = randomString(16);
    setState(newState);
    setNonce(newNonce);

    // Determine the scope based on whether custom API is selected
    let finalScopes = scopes;
    if (useCustomApi && customApiAppIdUri.trim()) {
      // For custom API, use user-specified scopes or .default as fallback
      const trimmedScopes = customApiScopes.trim();
      
      if (trimmedScopes) {
        // User provided specific scopes
        // Check if they provided full scope URIs or just scope names
        const apiUri = customApiAppIdUri.trim();
        
        // Split multiple scopes and process each one
        const scopeParts = trimmedScopes.split(/\s+/).filter(s => s);
        const processedScopes = scopeParts.map(scope => {
          // If scope already starts with the API URI or is a full URI (contains ://), use as-is
          if (scope.startsWith(apiUri) || scope.includes('://') || scope.includes('/')) {
            return scope;
          }
          // Otherwise, prepend the API URI
          return `${apiUri}/${scope}`;
        });
        
        finalScopes = processedScopes.join(' ');
      } else {
        // No custom scopes specified, use .default
        finalScopes = `${customApiAppIdUri.trim()}/.default`;
      }
    }

    const params: Record<string, string> = {
      client_id: selectedApp.appId,
      response_type: 'code',
      redirect_uri: redirectUri,
      response_mode: 'query',
      scope: finalScopes,
      state: newState,
      nonce: newNonce,
      prompt: 'select_account'
    };

    if (usePKCE) {
      const { challenge } = await generatePKCE();
      params.code_challenge = challenge;
      params.code_challenge_method = 'S256';
    }

    const authority = `https://login.microsoftonline.com/${selectedTenant || 'common'}`;
    const queryString = new URLSearchParams(params).toString();
    const url = `${authority}/oauth2/v2.0/authorize?${queryString}`;
    
    setAuthorizationUrl(url);
    setActiveStep(1);
  };

  // Step 2: Open authorization popup
  const openAuthorizationPopup = () => {
    if (!authorizationUrl) return;

    const w = 500;
    const h = 700;
    const y = window.top ? Math.max(0, (window.top.outerHeight - h) / 2) : 0;
    const x = window.top ? Math.max(0, (window.top.outerWidth - w) / 2) : 0;

    popupRef.current = window.open(
      authorizationUrl,
      'confidential-auth',
      `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes`
    );

    if (!popupRef.current) {
      setError('Popup blocked. Please allow popups and try again.');
    } else {
      setActiveStep(2);
    }
  };

  // Step 3: Exchange code for tokens
  const exchangeCodeForTokens = async () => {
    if (!authorizationCode || !selectedApp?.appId || !clientSecret) {
      setError('Missing required parameters for code exchange');
      return;
    }

    setExchanging(true);
    setError(null);

    try {
      // Determine the correct API URL based on the current origin
      const currentOrigin = window.location.origin;
      const isLocalhost = currentOrigin.includes('localhost');
      
      // For production Azure Static Web Apps, always use relative /api path
      // For localhost development, try explicit URL first, then fallback
      const apiUrls = isLocalhost
        ? [
            `${process.env.REACT_APP_API_URL || 'http://localhost:7071'}/api/exchange-code`,
            '/api/exchange-code'
          ]
        : [
            '/api/exchange-code',
            'http://localhost:7071/api/exchange-code'
          ];

      let response: Response | null = null;
      let lastError: string | null = null;

      for (const url of apiUrls) {
        try {
          console.log('Attempting API call to:', url);
          
          response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              code: authorizationCode,
              clientId: selectedApp.appId,
              clientSecret: clientSecret,
              redirectUri: redirectUri,
              tenantId: selectedTenant || 'common',
              codeVerifier: usePKCE ? codeVerifier : undefined
            })
          });

          console.log('Response status:', response.status, 'from', url);
          
          // Check if response is HTML (error page)
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('text/html')) {
            console.warn('Received HTML from', url, '- trying next URL');
            continue;
          }

          // Got a valid response, break the loop
          break;
        } catch (err: any) {
          console.warn('Failed to connect to', url, ':', err.message);
          lastError = err.message;
          continue;
        }
      }

      if (!response) {
        setError(`Could not connect to API. Make sure Azure Functions is running on port 7071. Last error: ${lastError}`);
        return;
      }

      const data = await response.json();
      console.log('Token exchange response:', data);

      if (!response.ok || data.error) {
        setError(data.error_description || data.error || 'Token exchange failed');
        setTokenResponse(data);
      } else {
        console.log('Setting token response:', data); // Debug log
        setTokenResponse(data);
        setActiveStep(4);
      }
    } catch (err: any) {
      setError(`Exchange error: ${err.message}`);
    } finally {
      setExchanging(false);
    }
  };

  // Decode JWT
  const decodeJwt = (token: string) => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(payload)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => alert(`${label} copied to clipboard`),
      () => alert('Failed to copy')
    );
  };

  // Reset flow
  const resetFlow = () => {
    setActiveStep(0);
    setAuthorizationUrl('');
    setAuthorizationCode('');
    setTokenResponse(null);
    setError(null);
    setState('');
    setNonce('');
    setCodeVerifier('');
    setCodeChallenge('');
    setClientSecret('');
    setShowSecret(false);
  };

  const decodedIdToken = tokenResponse?.id_token ? decodeJwt(tokenResponse.id_token) : null;
  const decodedAccessToken = tokenResponse?.access_token ? decodeJwt(tokenResponse.access_token) : null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <IconButton
            onClick={onBack}
            sx={{
              mr: 2,
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 400,
                color: '#1976d2',
                fontSize: '1.75rem'
              }}
            >
              Confidential Client Auth Tester
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              Test and inspect OAuth flows for web applications (confidential clients)
            </Typography>
          </Box>

          <Chip 
            label="In Preview" 
            color="info" 
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Card>

      {/* Critical Warning */}
      <Card variant="outlined" sx={{ mb: 0, borderRadius: 2 }}>
        <Accordion>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              backgroundColor: '#fff3e0',
              '&:hover': { backgroundColor: '#ffe0b2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 1, color: '#ed6c02' }} />
              <Typography sx={{ fontWeight: 600, color: '#e65100' }}>
                Testing and Debugging Only
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: '#fff3e0' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This tool shows <strong>real OAuth tokens</strong> for inspection purposes. In production web applications:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
              <li>Authorization code exchange happens <strong>server-side only</strong></li>
              <li>Access tokens and refresh tokens are <strong>never exposed to the browser</strong></li>
              <li>Tokens are stored in <strong>server session storage</strong> (Redis, database, memory)</li>
              <li>Browser receives only a <strong>secure session cookie</strong></li>
            </Box>
            <Typography variant="body2">
              This tool temporarily exposes tokens in the browser for debugging. <strong>Do not use this pattern in production!</strong>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* Main Tabs */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="🔧 Flow Debugger" />
          <Tab label="📚 How It Works" />
          <Tab label="⚖️ SPA vs Web App" />
        </Tabs>

        {/* Tab 1: Flow Debugger */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            {/* App Selection */}
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={searchableApps}
                value={selectedSearchApp}
                onChange={handleEnhancedApplicationChange}
                getOptionLabel={(option) => option.displayName}
                filterOptions={filterApplications}
                loading={searchLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Web Applications"
                    placeholder="Type application name, client ID, or app type..."
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <SearchIcon sx={{ mr: 1, color: 'action.disabled' }} />
                      )
                    }}
                    sx={{
                      mt: 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'transparent',
                          border: '1px solid rgba(0, 0, 0, 0.12)'
                        }}
                      >
                        {getAppTypeIcon(option.appType)}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.displayName}
                          </Typography>
                          <Chip
                            label={option.appType}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.75rem',
                              backgroundColor: getAudienceColor(option.signInAudience) + '20',
                              color: getAudienceColor(option.signInAudience),
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {option.appId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.signInAudience}
                          </Typography>
                          {option.hasSecrets && (
                            <>
                              <Typography variant="caption" color="text.secondary">•</Typography>
                              <Chip
                                label="Has Secrets"
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.625rem',
                                  backgroundColor: '#4caf50',
                                  color: 'white'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                )}
                noOptionsText={
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No web applications found matching your search.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try searching by app name, client ID, or app type.
                    </Typography>
                  </Box>
                }
                sx={{ mt: 2 }}
              />
              
              {activeStep > 0 && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={resetFlow}
                  sx={{
                    mt: 2,
                    color: 'white',
                    backgroundColor: '#d32f2f',
                    '&:hover': {
                      backgroundColor: '#c62828'
                    }
                  }}
                >
                  Start New Test
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Configuration Section */}
            <Accordion 
              defaultExpanded={!!selectedApp}
              sx={{
                bgcolor: '#f8f9fa',
                '&:before': {
                  display: 'none',
                },
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: '#f0f2f5',
                  '&:hover': {
                    bgcolor: '#e8eaed'
                  }
                }}
              >
                <Typography variant="h6">⚙️ Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fafbfc', pt: 3 }}>
                <Stack spacing={3}>
                  {/* Client Secret */}
                  <TextField
                    fullWidth
                    required
                    type={showSecret ? 'text' : 'password'}
                    label="Client Secret"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    helperText="Enter a valid client secret for this application"
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => setShowSecret(!showSecret)} edge="end">
                          {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      )
                    }}
                  />

                  {/* Tenant */}
                  <TextField
                    fullWidth
                    label="Tenant ID"
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                    helperText="Use 'common', 'organizations', or specific tenant ID"
                  />

                  {/* Redirect URI */}
                  <TextField
                    fullWidth
                    label="Redirect URI"
                    value={redirectUri}
                    onChange={(e) => setRedirectUri(e.target.value)}
                    helperText="Must match a redirect URI configured in the app"
                  />

                  {/* Target API Selection */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Target API
                    </Typography>
                    <ToggleButtonGroup
                      value={useCustomApi ? 'custom' : 'graph'}
                      exclusive
                      onChange={(_, val) => {
                        if (val !== null) {
                          setUseCustomApi(val === 'custom');
                        }
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="graph">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CloudIcon fontSize="small" />
                          Microsoft Graph (Default)
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="custom">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ApiIcon fontSize="small" />
                          Custom API
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    
                    {useCustomApi && (
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <AlertTitle>Custom API Requirements</AlertTitle>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Before testing, ensure your API application has:
                          </Typography>
                          <Box component="ol" sx={{ m: 0, pl: 2, fontSize: '0.875rem' }}>
                            <li>Application ID URI set to <code>api://{'{clientId}'}</code> in "Expose an API"</li>
                            <li>Exposed scopes defined (e.g., <code>user_impersonation</code>)</li>
                            <li>This client app granted permissions to those scopes</li>
                            <li>Admin consent granted for the permissions</li>
                          </Box>
                        </Alert>
                        <TextField
                          fullWidth
                          label="Custom API App ID URI"
                          value={customApiAppIdUri}
                          onChange={(e) => setCustomApiAppIdUri(e.target.value)}
                          placeholder="e.g., api://your-api-client-id or https://yourdomain.com"
                          helperText="The Application ID URI of the API (found in 'Expose an API' section)"
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Custom API Scopes"
                          value={customApiScopes}
                          onChange={(e) => setCustomApiScopes(e.target.value)}
                          placeholder="e.g., user_impersonation or api://your-api-id/user_impersonation"
                          helperText="Enter scope name(s) (e.g., 'user_impersonation') or full scope URI. Will auto-prefix with App ID URI if needed."
                          multiline
                          rows={1}
                          sx={{ mb: 2 }}
                        />
                      </>
                    )}
                    
                    <Typography variant="caption" color="text.secondary">
                      {useCustomApi 
                        ? '🔧 Testing against a custom API. Make sure the API has exposed scopes and this app has been granted access. You can specify exact scopes or leave empty to use .default.'
                        : '☁️ Testing against Microsoft Graph API with standard scopes.'
                      }
                    </Typography>
                  </Box>

                  {/* Scopes - only show if not using custom API */}
                  {!useCustomApi && (
                    <TextField
                      fullWidth
                      label="Scopes"
                      value={scopes}
                      onChange={(e) => setScopes(e.target.value)}
                      helperText="Space-separated scopes (include 'offline_access' for refresh token)"
                    />
                  )}

                  {/* PKCE Toggle */}
                  <Box>
                    <ToggleButtonGroup
                      value={usePKCE}
                      exclusive
                      onChange={(_, val) => val !== null && setUsePKCE(val)}
                    >
                      <ToggleButton value={true}>
                        ✅ Use PKCE (Recommended)
                      </ToggleButton>
                      <ToggleButton value={false}>
                        ⚠️ No PKCE
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      PKCE adds extra security even for confidential clients
                    </Typography>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 2 }} />

            {/* Flow Execution Section */}
            <Accordion 
              defaultExpanded={activeStep > 0}
              sx={{
                bgcolor: '#f8f9fa',
                '&:before': {
                  display: 'none',
                },
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: '#f0f2f5',
                  '&:hover': {
                    bgcolor: '#e8eaed'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon sx={{ color: '#1976d2' }} />
                  <Typography variant="h6">OAuth Flow Execution</Typography>
                  {activeStep > 0 && (
                    <Chip 
                      label={`Step ${activeStep} of 4`} 
                      size="small" 
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fafbfc', pt: 3 }}>
                {/* Debug indicator */}
                {activeStep >= 3 && authorizationCode && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ✅ Authorization code received! Scroll down to Step 3 to exchange it for tokens.
                  </Alert>
                )}

                {/* Flow Stepper */}
                <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: Generate URL */}
              <Step>
                <StepLabel>Generate Authorization URL</StepLabel>
                <StepContent>
                  <Button
                    variant="contained"
                    onClick={generateAuthUrl}
                    disabled={!selectedApp || !clientSecret}
                    sx={{ mt: 2 }}
                  >
                    Generate Authorization URL
                  </Button>
                </StepContent>
              </Step>

              {/* Step 2: Authorize */}
              <Step>
                <StepLabel>User Authorization</StepLabel>
                <StepContent>
                  {authorizationUrl && (
                    <Box>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Authorization URL:
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {authorizationUrl}
                        </Typography>
                      </Paper>
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          onClick={openAuthorizationPopup}
                          startIcon={<VpnKeyIcon />}
                        >
                          Open Authorization Popup
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => copyToClipboard(authorizationUrl, 'Authorization URL')}
                          startIcon={<ContentCopyIcon />}
                        >
                          Copy URL
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </StepContent>
              </Step>

              {/* Step 3: Code Received */}
              <Step>
                <StepLabel>Authorization Code Received</StepLabel>
                <StepContent>
                  {(() => {
                    console.log('[Step 3 Render] activeStep:', activeStep, 'authorizationCode:', authorizationCode ? `${authorizationCode.substring(0, 20)}...` : 'null');
                    
                    if (!authorizationCode) {
                      return (
                        <Typography color="text.secondary">
                          Waiting for authorization code from callback...
                        </Typography>
                      );
                    }
                    
                    return (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <AlertTitle>✓ Code Received</AlertTitle>
                          Authorization code received from Microsoft successfully.
                        </Alert>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="caption" display="block" gutterBottom>
                            Authorization Code:
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {authorizationCode}
                          </Typography>
                        </Paper>
                        <Button
                          variant="contained"
                          onClick={exchangeCodeForTokens}
                          disabled={exchanging}
                          startIcon={exchanging ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
                        >
                          {exchanging ? 'Exchanging...' : 'Exchange Code for Tokens'}
                        </Button>
                      </Box>
                    );
                  })()}
                </StepContent>
              </Step>

              {/* Step 4: Tokens */}
              <Step>
                <StepLabel>Tokens Received</StepLabel>
                <StepContent>
                  {(() => {
                    console.log('[Step 4 Render] activeStep:', activeStep, 'tokenResponse:', tokenResponse);
                    
                    if (!tokenResponse && activeStep >= 4) {
                      return (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <AlertTitle>⚠️ No Token Data</AlertTitle>
                          Step 4 reached but tokenResponse is empty. This might indicate:
                          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>The token exchange API call failed</li>
                            <li>The API returned an error response</li>
                            <li>Check browser console and network tab for errors</li>
                          </ul>
                          <Button variant="outlined" size="small" onClick={resetFlow} sx={{ mt: 1 }}>
                            Start Over
                          </Button>
                        </Alert>
                      );
                    }
                    
                    if (!tokenResponse) {
                      return null;
                    }
                    
                    if (!tokenResponse.access_token) {
                      return (
                        <Alert severity="warning">
                          Token response received but no access_token found. Response keys: {Object.keys(tokenResponse).join(', ')}
                        </Alert>
                      );
                    }
                    
                    return (
                      <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                          <AlertTitle>✓ Token Exchange Successful</AlertTitle>
                          Tokens received successfully. View decoded claims below.
                        </Alert>

                        {/* ID Token */}
                        {tokenResponse.id_token && (
                          <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>🆔 ID Token</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={2}>
                                <Button
                                  size="small"
                                  onClick={() => copyToClipboard(tokenResponse.id_token, 'ID Token')}
                                  startIcon={<ContentCopyIcon />}
                                >
                                  Copy Token
                                </Button>
                                {decodedIdToken && (
                                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="subtitle2" gutterBottom>Decoded Claims:</Typography>
                                    <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 320 }}>
                                      {JSON.stringify(decodedIdToken, null, 2)}
                                    </pre>
                                  </Paper>
                                )}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* Access Token */}
                        <Accordion sx={{ mb: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>🔑 Access Token</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={2}>
                              <Button
                                size="small"
                                onClick={() => copyToClipboard(tokenResponse.access_token, 'Access Token')}
                                startIcon={<ContentCopyIcon />}
                              >
                                Copy Token
                              </Button>
                              {decodedAccessToken && (
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                  <Typography variant="subtitle2" gutterBottom>Decoded Claims:</Typography>
                                  <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 320 }}>
                                    {JSON.stringify(decodedAccessToken, null, 2)}
                                  </pre>
                                </Paper>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>

                        <Box sx={{ mt: 3 }}>
                          <Button variant="contained" onClick={resetFlow}>
                            Start New Test
                          </Button>
                        </Box>
                      </Box>
                    );
                  })()}
                </StepContent>
              </Step>
            </Stepper>
              </AccordionDetails>
            </Accordion>

            {/* Fallback: Show exchange button if Step 3 is active but stepper content not visible */}
            {activeStep === 3 && authorizationCode && (
              <Box sx={{ mt: 3, p: 3, border: '2px solid #4caf50', borderRadius: 2, bgcolor: '#f1f8f4' }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>✓ Authorization Code Received</AlertTitle>
                  Authorization code received from Microsoft successfully. Click below to exchange it for tokens.
                </Alert>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
                  <Typography variant="caption" display="block" gutterBottom fontWeight="bold">
                    Authorization Code:
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {authorizationCode}
                  </Typography>
                </Paper>
                <Button
                  variant="contained"
                  size="large"
                  onClick={exchangeCodeForTokens}
                  disabled={exchanging}
                  startIcon={exchanging ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
                  sx={{ fontWeight: 600 }}
                >
                  {exchanging ? 'Exchanging Code for Tokens...' : 'Exchange Code for Tokens'}
                </Button>
              </Box>
            )}

            {/* Fallback: Show tokens if Step 4 is active but stepper content not visible */}
            {activeStep === 4 && tokenResponse && tokenResponse.access_token && (
              <Box sx={{ mt: 3, p: 3, border: '2px solid #2196f3', borderRadius: 2, bgcolor: '#e3f2fd' }}>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <AlertTitle>✓ Token Exchange Successful</AlertTitle>
                  Tokens received successfully. View decoded claims below.
                </Alert>

                {/* ID Token */}
                {tokenResponse.id_token && (
                  <Accordion sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 500 }}>🆔 ID Token</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => copyToClipboard(tokenResponse.id_token, 'ID Token')}
                          startIcon={<ContentCopyIcon />}
                        >
                          Copy Token
                        </Button>
                        {decodedIdToken && (
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                            <Typography variant="subtitle2" gutterBottom>Decoded Claims:</Typography>
                            <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 320 }}>
                              {JSON.stringify(decodedIdToken, null, 2)}
                            </pre>
                          </Paper>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Access Token */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 500 }}>🔑 Access Token</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copyToClipboard(tokenResponse.access_token, 'Access Token')}
                        startIcon={<ContentCopyIcon />}
                      >
                        Copy Token
                      </Button>
                      {decodedAccessToken && (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                          <Typography variant="subtitle2" gutterBottom>Decoded Claims:</Typography>
                          <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 320 }}>
                            {JSON.stringify(decodedAccessToken, null, 2)}
                          </pre>
                        </Paper>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" size="large" onClick={resetFlow} sx={{ fontWeight: 600 }}>
                    Start New Test
                  </Button>
                </Box>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>Error</AlertTitle>
                  {error}
                </Alert>
                <Button variant="contained" onClick={resetFlow}>
                  Start New Test
                </Button>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Tab 2: Educational Content */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <SchoolIcon sx={{ mr: 1 }} />
              Understanding Confidential Client Flows
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              This tool demonstrates the <strong>OAuth 2.0 Authorization Code Flow</strong> for confidential clients (web applications). 
              Unlike Single Page Applications (SPAs), confidential clients can securely store secrets server-side.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Section 1: The Authorization Code Flow */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#1976d2' }}>
              <TimelineIcon sx={{ mr: 1 }} />
              The Authorization Code Flow
            </Typography>
            
            <Box component="ol" sx={{ pl: 3, '& li': { mb: 2 } }}>
              <li>
                <strong>User Initiates Login:</strong> User clicks "Sign In" in your web application
              </li>
              <li>
                <strong>Redirect to Authorization Server:</strong> Browser redirects to Microsoft identity platform with:
                <Box component="ul" sx={{ mt: 1, '& li': { fontSize: '0.875rem' } }}>
                  <li>client_id (your application's ID)</li>
                  <li>redirect_uri (where to send the code)</li>
                  <li>scope (permissions requested)</li>
                  <li>state (CSRF protection)</li>
                  <li>code_challenge (PKCE - optional but recommended)</li>
                </Box>
              </li>
              <li>
                <strong>User Authenticates & Consents:</strong> User signs in and grants permissions
              </li>
              <li>
                <strong>Authorization Code Returned:</strong> Browser redirects back to your redirect_uri with:
                <Box component="ul" sx={{ mt: 1, '& li': { fontSize: '0.875rem' } }}>
                  <li>code (short-lived authorization code, ~10 minutes)</li>
                  <li>state (must match original value)</li>
                </Box>
              </li>
              <li>
                <strong>Exchange Code for Tokens (Server-Side):</strong> Your backend makes a POST to token endpoint with:
                <Box component="ul" sx={{ mt: 1, '& li': { fontSize: '0.875rem' } }}>
                  <li>code (the authorization code)</li>
                  <li>client_id + client_secret (proves it's your server)</li>
                  <li>redirect_uri (must match original)</li>
                  <li>code_verifier (if using PKCE)</li>
                </Box>
              </li>
              <li>
                <strong>Tokens Received:</strong> Microsoft returns:
                <Box component="ul" sx={{ mt: 1, '& li': { fontSize: '0.875rem' } }}>
                  <li>id_token (user identity claims)</li>
                  <li>access_token (for calling APIs)</li>
                  <li>refresh_token (for getting new tokens)</li>
                </Box>
              </li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 2: Security Benefits */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2e7d32' }}>
              <SecurityIcon sx={{ mr: 1 }} />
              Why This Flow is More Secure
            </Typography>
            
            <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
              <li>
                <strong>Client Secret Protected:</strong> The client_secret never leaves your server, making it impossible for attackers to steal from browser
              </li>
              <li>
                <strong>Authorization Code is Single-Use:</strong> Even if intercepted, the code is useless without the client_secret
              </li>
              <li>
                <strong>Tokens Stored Server-Side:</strong> Access tokens and refresh tokens never reach the browser
              </li>
              <li>
                <strong>Session Cookie Only:</strong> Browser only gets a secure, HttpOnly session cookie
              </li>
              <li>
                <strong>PKCE Protection:</strong> Optional code_challenge/code_verifier prevents authorization code interception attacks
              </li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 3: What This Tool Does */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>⚠️ What This Tool Does (Not Production Pattern)</AlertTitle>
              <Typography variant="body2" paragraph>
                This tool <strong>simulates</strong> the confidential client flow, but performs the code exchange <strong> in the browser </strong> 
                via a backend API endpoint. This is for <strong>testing and debugging only</strong>.
              </Typography>
              <Typography variant="body2">
                In production, the entire code exchange happens server-side in your web application's backend, 
                and tokens are stored in server session storage (Redis, database, or memory). The browser never sees the tokens.
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Production Architecture:
            </Typography>
            
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.85rem' }}>
              1. Browser → Your Backend → /login<br />
              2. Your Backend → Redirect to Microsoft with auth params<br />
              3. Microsoft → User authenticates<br />
              4. Microsoft → Redirect to Your Backend with code<br />
              5. Your Backend → POST to Microsoft token endpoint (with secret)<br />
              6. Microsoft → Returns tokens to Your Backend<br />
              7. Your Backend → Store tokens in session (Redis/DB)<br />
              8. Your Backend → Set session cookie in browser<br />
              9. Browser → Uses session cookie for authenticated requests
            </Paper>

            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              💡 <strong>Key Takeaway:</strong> In production web apps, tokens never touch the browser. 
              All OAuth token operations happen server-side, and the browser only receives a session identifier.
            </Typography>
          </CardContent>
        </TabPanel>

        {/* Tab 3: SPA vs Web App Comparison */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CompareArrowsIcon sx={{ mr: 1 }} />
              SPA vs Web App: Architecture Comparison
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              Understanding the differences between Single Page Applications (SPAs) and traditional Web Applications 
              is crucial for choosing the right authentication pattern for your project.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Comparison Table */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead" sx={{ bgcolor: '#f5f5f5' }}>
                    <Box component="tr">
                      <Box component="th" sx={{ p: 2, textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Aspect</Box>
                      <Box component="th" sx={{ p: 2, textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#1976d2' }}>SPA (React, Angular, Vue)</Box>
                      <Box component="th" sx={{ p: 2, textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#2e7d32' }}>Web App (ASP.NET, Node.js)</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Client Type</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Public Client</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Confidential Client</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>OAuth Flow</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Authorization Code + PKCE</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Authorization Code + Client Secret</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Client Secret</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>❌ Cannot store (runs in browser)</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>✅ Stored securely on server</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Token Storage</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Browser memory (sessionStorage)</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Server session (Redis, DB, memory)</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Token Visibility</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>⚠️ Visible to JavaScript</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>✅ Never exposed to browser</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Refresh Tokens</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>✅ Supported (with rotation policy)</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>✅ Safe (stored server-side)</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>XSS Attack Risk</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>⚠️ Higher (tokens in browser)</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>✅ Lower (tokens on server)</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Session Management</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Client-side (MSAL library)</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Server-side (session store)</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>Browser Receives</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Access & ID tokens directly</Box>
                      <Box component="td" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>Session cookie only</Box>
                    </Box>
                    <Box component="tr">
                      <Box component="td" sx={{ p: 2, fontWeight: 500 }}>Best For</Box>
                      <Box component="td" sx={{ p: 2 }}>Client-side apps, mobile web, static hosting</Box>
                      <Box component="td" sx={{ p: 2 }}>Enterprise apps, high-security scenarios</Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* When to Use Each */}
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              When to Use Each Pattern
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Alert severity="info" icon={<InfoIcon />}>
                <AlertTitle>Choose SPA Pattern When:</AlertTitle>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>Building a client-side only application (React, Vue, Angular)</li>
                  <li>Using static hosting (CDN, GitHub Pages, Azure Static Web Apps)</li>
                  <li>No backend server or minimal backend requirements</li>
                  <li>PKCE provides sufficient security for your use case</li>
                </Box>
              </Alert>

              <Alert severity="success" icon={<CheckCircleIcon />}>
                <AlertTitle>Choose Web App Pattern When:</AlertTitle>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>You have a backend server (Node.js, ASP.NET, Python, Java)</li>
                  <li>Handling sensitive data or high-security requirements</li>
                  <li>Need long-lived sessions with refresh token rotation</li>
                  <li>Enterprise applications with strict security policies</li>
                  <li>Want to minimize XSS attack surface</li>
                  <li>Server-side rendering (SSR) is part of your architecture</li>
                </Box>
              </Alert>
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Key Security Differences */}
            <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f' }}>
              Key Security Differences
            </Typography>

            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ff9800', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                SPA Security Considerations:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: '0.875rem' }}>
                <li>Tokens stored in browser memory are vulnerable to XSS attacks</li>
                <li>No client secret = anyone can impersonate your app</li>
                <li>PKCE is mandatory but only protects against code interception</li>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Web App Security Advantages:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: '0.875rem' }}>
                <li>Client secret proves your server's identity to Microsoft</li>
                <li>Tokens never exposed to browser = immune to XSS token theft</li>
                <li>Server can revoke sessions immediately (better control)</li>
                <li>Session cookies can be HttpOnly, Secure, and SameSite protected</li>
              </Box>
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* Migration Path */}
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Migration Path: SPA → Web App
            </Typography>

            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              If you're currently using the SPA pattern but need better security, here's how to migrate:
            </Typography>

            <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
              <li>
                <strong>Add Backend Server:</strong> Introduce a backend (Node.js, ASP.NET, etc.) to handle authentication
              </li>
              <li>
                <strong>Create Client Secret:</strong> Generate a client secret in Azure portal for your app registration
              </li>
              <li>
                <strong>Move Token Exchange Server-Side:</strong> Handle authorization code exchange in your backend, not browser
              </li>
              <li>
                <strong>Implement Session Storage:</strong> Store tokens in Redis, database, or server memory
              </li>
              <li>
                <strong>Issue Session Cookies:</strong> Return HttpOnly cookies to browser instead of tokens
              </li>
              <li>
                <strong>Update API Calls:</strong> Have browser call your backend; backend uses stored tokens to call Microsoft APIs
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <AlertTitle>💡 Hybrid Approach</AlertTitle>
              <Typography variant="body2">
                Some applications use a hybrid model: SPA frontend with a backend-for-frontend (BFF) pattern. 
                The BFF handles all OAuth flows and token management, while the SPA remains stateless and uses session cookies.
              </Typography>
            </Alert>
          </CardContent>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default ConfidentialClientAuthDebugger;
